// src/oura/ui/Ring.tsx
//
// The two chart pieces the kit owns. Rings are for the three engines on
// Visibility and the four PageSpeed gauges only (YW, 25 Sep): anything else
// is a number, not a dial.

import type { ReactNode } from "react";
import { cn } from "../lib/cn";

/**
 * A ring filled to `value` percent (0 to 100), with `children` centred in it.
 * `null` is not measured: the track only, dashed when `dashed` is set, and
 * never a ring at zero.
 */
export function Ring({
    value,
    size = 64,
    stroke = 7,
    color = "#0B49FE",
    dashed,
    children,
}: {
    value: number | null;
    size?: number;
    stroke?: number;
    /** Stroke colour of the filled arc (an AREA hex, or a band colour). */
    color?: string;
    dashed?: boolean;
    children?: ReactNode;
}) {
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const fill = value === null ? 0 : Math.max(0, Math.min(100, value)) / 100;
    return (
        <div className="relative shrink-0" style={{ width: size, height: size }}>
            <svg aria-hidden viewBox={`0 0 ${size} ${size}`} width={size} height={size} className="-rotate-90">
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    fill="none"
                    stroke={dashed ? "#D1D5DB" : "#F3F4F6"}
                    strokeWidth={dashed ? 2 : stroke}
                    strokeDasharray={dashed ? "3 4" : undefined}
                />
                {fill > 0 && (
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={r}
                        fill="none"
                        stroke={color}
                        strokeWidth={stroke}
                        strokeLinecap="round"
                        strokeDasharray={`${(c * fill).toFixed(1)} ${c.toFixed(1)}`}
                    />
                )}
            </svg>
            <div className="absolute inset-0 grid place-items-center text-center leading-none">{children}</div>
        </div>
    );
}

/**
 * "▲ 2 since 18 Aug". Renders NOTHING unless `comparable` is exactly true.
 *
 * A change is only a change where the method stayed the same (rule 2). The
 * caller decides that through trendBreak.ts (`deltaCrossesBreak`), and a
 * missing answer is treated as "not comparable", never as "no change".
 */
export function Delta({ value, comparable, suffix, className }: { value: number; comparable: boolean; suffix?: ReactNode; className?: string }) {
    // One mark for a change everywhere, the triangle (Jack, 22 Oct): merchants
    // had an arrow here beside a ▲ on the very next card.
    if (comparable !== true) return null;
    const dir = value > 0 ? "up" : value < 0 ? "down" : "flat";
    return (
        <span
            className={cn(
                "font-medium tabular-nums",
                dir === "up" ? "text-emerald-700" : dir === "down" ? "text-rose-700" : "text-gray-500",
                className,
            )}
        >
            {dir === "flat" ? (
                "No change"
            ) : (
                <>
                    <span aria-hidden>{dir === "up" ? "▲" : "▼"} </span>
                    <span className="sr-only">{dir === "up" ? "Up " : "Down "}</span>
                    {Math.abs(value)}
                </>
            )}
            {suffix && <> {suffix}</>}
        </span>
    );
}
